import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";

const AuthContext = createContext(null);
const SESSION_KEY = "proxiq.session";

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = unknown, false = logged out, obj = logged in
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Only probe /auth/me if we previously marked the user as signed in.
        // Avoids a noisy 401 in the DevTools console on the public /login page.
        if (typeof window !== "undefined" && !localStorage.getItem(SESSION_KEY)) {
            setUser(false);
            setLoading(false);
            return;
        }
        let mounted = true;
        api.get("/auth/me")
            .then((res) => {
                if (!mounted) return;
                setUser(res.data);
            })
            .catch(() => {
                if (!mounted) return;
                try { localStorage.removeItem(SESSION_KEY); } catch {}
                setUser(false);
            })
            .finally(() => mounted && setLoading(false));
        return () => {
            mounted = false;
        };
    }, []);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            try { localStorage.setItem(SESSION_KEY, "1"); } catch {}
            setUser(data);
            return { ok: true };
        } catch (e) {
            return {
                ok: false,
                error: formatApiError(e.response?.data?.detail) || e.message,
            };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {}
        try { localStorage.removeItem(SESSION_KEY); } catch {}
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
